import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, App, Button, Empty, Flex, Input, InputNumber, Modal, Result, Skeleton, Space, Table, Tabs, Tag, Typography } from "antd";
import { useDeferredValue, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { ActivityTable } from "../ActivityLog";
import { PlatformLabel } from "../channels/PlatformBadge";
import { MappedTo, useRefreshMapping, usePlatformActions, type PlatformSKU } from "./mapping";
import { PriceSyncTag, usePriceErrorText } from "./prices";

/** Detail SKU (App Flow §7), its own work tab named after the code. */
export function SkuDetail() {
  const { skuID = "" } = useParams();
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const errorText = useErrorText();
  const manage = can("catalog.manage");
  const [picking, setPicking] = useState(false);

  const sku = useQuery({
    queryKey: ["skus", "detail", skuID],
    queryFn: () => unwrap(api.GET("/v1/skus/{skuID}", { params: { path: { skuID } } })),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  useTabTitle(sku.data?.code);

  if (sku.isError) {
    return sku.error instanceof ApiError && sku.error.status === 404 ? (
      <Result status="404" title={t("page.notFound.title")} />
    ) : (
      <Result status="error" title={errorText(sku.error)} />
    );
  }
  const s = sku.data;
  if (!s) return <Skeleton active />;

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="baseline" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {s.code}
          </Typography.Title>
          <Typography.Text>{s.name}</Typography.Text>
          {s.status === "active" ? <Tag color="success">{t("catalog.active")}</Tag> : <Tag>{t("catalog.archived")}</Tag>}
          {s.tracking !== "none" && <Tag color="processing">{t(`catalog.tracking.${s.tracking}`)}</Tag>}
          {s.kind === "bundle" && <Tag color="purple">{t("catalog.bundle")}</Tag>}
        </Space>
        <Space>
          {can("inventory.view") && <Button onClick={() => navigate(`/app/inventory?sku=${s.id}`)}>{t("catalog.detail.stock")}</Button>}
          {manage && s.status === "active" && (
            <Button type="primary" onClick={() => setPicking(true)}>
              {t("catalog.detail.mapToPlatform")}
            </Button>
          )}
        </Space>
      </Flex>
      {s.kind === "bundle" && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("catalog.bundleMadeOf", { items: s.components.map((c) => `${c.quantity}× ${c.code} (${c.name})`).join(" + ") })}
          description={t("catalog.bundleHelp")}
        />
      )}
      <Tabs
        items={[
          { key: "mapping", label: t("catalog.detail.mapping"), children: <Mappings skuId={s.id} /> },
          { key: "prices", label: t("price.tab"), children: <Prices skuId={s.id} /> },
          ...(can("logs.all") ? [{ key: "log", label: t("catalog.detail.log"), children: <ActivityTable entityType="product" entityId={s.id} /> }] : []),
        ]}
      />
      <PickPlatform open={picking} skuId={s.id} code={s.code} name={s.name} onClose={() => setPicking(false)} />
    </>
  );
}

function Mappings({ skuId }: { skuId: string }) {
  const { t, formatMoney, formatNumber } = useI18n();
  const { can } = useAuth();
  const { untrack } = usePlatformActions();
  const list = useQuery({
    queryKey: ["platform-skus", "of", skuId],
    queryFn: () => unwrap(api.GET("/v1/catalog/platform-skus", { params: { query: { skuId, limit: 200 } } })),
  });
  return (
    <Table<PlatformSKU>
      rowKey="id"
      loading={list.isPending}
      dataSource={list.data?.items ?? []}
      pagination={false}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("catalog.detail.mappingEmpty")} /> }}
      columns={[
        {
          title: t("orders.store"),
          render: (_, p) => (
            <Space orientation="vertical" size={0}>
              <PlatformLabel platform={p.platform} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {p.shopName}
              </Typography.Text>
            </Space>
          ),
        },
        { title: t("catalog.platform.product"), render: (_, p) => [p.listingName, p.variant].filter(Boolean).join(" · ") },
        { title: t("catalog.platform.sellerSku"), dataIndex: "sellerSku", render: (v: string) => <span className="z-num">{v || "—"}</span> },
        { title: t("catalog.platform.price"), dataIndex: "price", align: "right", render: (v: number | null) => <span className="z-num">{v === null ? "—" : formatMoney(v, "IDR")}</span> },
        { title: t("catalog.platform.stock"), dataIndex: "platformStock", align: "right", render: (v: number | null) => <span className="z-num">{v === null ? "—" : formatNumber(v)}</span> },
        { title: "", dataIndex: "mappingSource", render: (v: string | null) => (v ? <Tag>{t(`catalog.mapping.${v === "auto_code" ? "auto_code" : "manual"}`)}</Tag> : null) },
        ...(can("catalog.manage")
          ? [
              {
                width: 90,
                render: (_: unknown, p: PlatformSKU) => (
                  <Button size="small" onClick={() => untrack([p.id], t("catalog.unmapTitle", { name: p.sellerSku || p.listingName }))}>
                    {t("catalog.unmap")}
                  </Button>
                ),
              },
            ]
          : []),
      ]}
    />
  );
}

/** Harga tab: the price on each store, edited one by one and sent to the marketplace. */
function Prices({ skuId }: { skuId: string }) {
  const { t, formatMoney } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const priceErrorText = usePriceErrorText();
  const refresh = useRefreshMapping();
  const [editing, setEditing] = useState<Record<string, number | null>>({});
  const list = useQuery({
    queryKey: ["platform-skus", "of", skuId],
    queryFn: () => unwrap(api.GET("/v1/catalog/platform-skus", { params: { query: { skuId, limit: 200 } } })),
    // Follow the marketplace's answer while a change is on its way.
    refetchInterval: (q) => (q.state.data?.items.some((p) => p.priceSync?.status === "pending") ? 2000 : false),
  });
  const send = useMutation({
    mutationFn: ({ id, price }: { id: string; price: number }) => unwrap(api.POST("/v1/prices", { body: { items: [{ channelSkuId: id, price }] } })),
    onSuccess: (_, v) => {
      void message.success(t("price.queued"));
      setEditing((e) => {
        const next = { ...e };
        delete next[v.id];
        return next;
      });
      refresh();
    },
    onError: (err) => void message.error(priceErrorText(err)),
  });
  const manage = can("catalog.manage");
  return (
    <Table<PlatformSKU>
      rowKey="id"
      loading={list.isPending}
      dataSource={list.data?.items ?? []}
      pagination={false}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("price.empty")} /> }}
      columns={[
        {
          title: t("orders.store"),
          render: (_, p) => (
            <Space orientation="vertical" size={0}>
              <PlatformLabel platform={p.platform} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {p.shopName}
              </Typography.Text>
            </Space>
          ),
        },
        { title: t("catalog.platform.product"), render: (_, p) => [p.listingName, p.variant].filter(Boolean).join(" · ") },
        {
          title: t("price.current"),
          dataIndex: "price",
          align: "right",
          width: 140,
          render: (v: number | null) => (
            <Typography.Text strong className="z-num">
              {v === null ? "—" : formatMoney(v, "IDR")}
            </Typography.Text>
          ),
        },
        { title: "", width: 170, render: (_, p) => <PriceSyncTag p={p} /> },
        ...(manage
          ? [
              {
                title: t("price.new"),
                width: 260,
                render: (_: unknown, p: PlatformSKU) =>
                  p.id in editing ? (
                    <Space.Compact>
                      <InputNumber<number>
                        autoFocus
                        min={100}
                        precision={0}
                        style={{ width: 150 }}
                        value={editing[p.id]}
                        onChange={(v) => setEditing((e) => ({ ...e, [p.id]: v }))}
                        onPressEnter={() => editing[p.id] && send.mutate({ id: p.id, price: editing[p.id]! })}
                      />
                      <Button
                        type="primary"
                        disabled={!editing[p.id] || editing[p.id]! < 100}
                        loading={send.isPending && send.variables?.id === p.id}
                        onClick={() => send.mutate({ id: p.id, price: editing[p.id]! })}
                      >
                        {t("price.save")}
                      </Button>
                    </Space.Compact>
                  ) : (
                    <Button size="small" onClick={() => setEditing((e) => ({ ...e, [p.id]: p.price }))}>
                      {t("price.edit")}
                    </Button>
                  ),
              },
            ]
          : []),
      ]}
    />
  );
}

/** "Petakan ke platform": platform SKUs not mapped yet, most similar first. */
function PickPlatform({ open, skuId, code, name, onClose }: { open: boolean; skuId: string; code: string; name: string; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefreshMapping();
  const [search, setSearch] = useState("");
  const q = useDeferredValue(search.trim());
  const [selected, setSelected] = useState<string[]>([]);
  const candidates = useQuery({
    queryKey: ["platform-skus", "candidates", skuId, q],
    queryFn: () => unwrap(api.GET("/v1/catalog/platform-skus", { params: { query: { mapped: false, suggest: name, limit: 50, ...(q ? { q } : {}) } } })),
    enabled: open,
  });
  const map = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/catalog/platform-skus/map", { body: { ids: selected, skuId } })),
    onSuccess: () => {
      void message.success(t("catalog.mapped"));
      refresh();
      setSelected([]);
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open={open}
      width={860}
      title={t("catalog.detail.mapToPlatformTitle", { code })}
      okText={t("catalog.mapSelected", { n: selected.length })}
      okButtonProps={{ disabled: selected.length === 0 }}
      cancelText={t("common.cancel")}
      confirmLoading={map.isPending}
      onOk={() => map.mutate()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Flex justify="space-between" align="center" gap={12} wrap style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary">{t("catalog.detail.suggested")}</Typography.Text>
        <Input.Search allowClear placeholder={t("catalog.platform.search")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 280 }} />
      </Flex>
      <Table<PlatformSKU>
        size="small"
        rowKey="id"
        loading={candidates.isPending}
        dataSource={candidates.data?.items ?? []}
        pagination={false}
        scroll={{ y: 420 }}
        rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) }}
        columns={[
          { title: t("orders.store"), width: 160, render: (_, p) => <PlatformLabel platform={p.platform} /> },
          { title: t("catalog.platform.product"), render: (_, p) => [p.listingName, p.variant].filter(Boolean).join(" · ") },
          { title: t("catalog.platform.sellerSku"), dataIndex: "sellerSku", width: 150, render: (v: string) => <span className="z-num">{v || "—"}</span> },
          { title: t("catalog.platform.mappedTo"), width: 140, render: (_, p) => <MappedTo p={p} /> },
        ]}
      />
    </Modal>
  );
}
